import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Select, Button, Input } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useTickets } from "../../queries/tickets";
import { useProjects } from "../../queries/projects";
import { TicketProps, ProjectProps } from "../../types/interfaces";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { StatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";

/**
 * The list archetype: sticky page header, a filter toolbar with a live count,
 * then one full-bleed table. No Card wrapper — the table's own rules are the
 * container.
 */
export const TicketListPage = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const [projectId, setProjectId] = useState<string | undefined>();
  const [solved, setSolved] = useState<boolean | undefined>(false);
  const [query, setQuery] = useState("");

  const { data: tickets, isLoading, isError, error } = useTickets({ projectId, solved });
  const { data: projects } = useProjects();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tickets || []).filter((t: TicketProps) => (q ? t.subject.toLowerCase().includes(q) : true));
  }, [tickets, query]);

  return (
    <PageShell
      kind="list"
      kicker="Support"
      title={solved ? "Solved tickets" : "Open tickets"}
      subtitle={
        userData?.role === "final_user"
          ? "Anything you've raised with us, and where it stands."
          : undefined
      }
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/support/tickets/new")}>
          New ticket
        </Button>
      }
    >
      <Toolbar count={rows.length} unit="tickets">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--q-text-faint)" }} />}
          placeholder="Search subjects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        {userData?.role !== "final_user" && (
          <Select
            allowClear
            style={{ width: 200 }}
            placeholder="All projects"
            value={projectId}
            onChange={setProjectId}
            options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          />
        )}
        <Select
          style={{ width: 140 }}
          value={solved}
          onChange={setSolved}
          options={[
            { label: "Open", value: false },
            { label: "Solved", value: true },
          ]}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<TicketProps>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          onRow={(ticket) => ({
            onClick: () => navigate(`/support/tickets/${ticket._id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <EmptyState
                title={solved ? "No solved tickets yet" : "No open tickets"}
                description={
                  solved
                    ? "Tickets move here once they're marked solved."
                    : "Nothing is waiting on you. New requests from clients will appear here first."
                }
                actions={
                  !solved && (
                    <Button type="primary" onClick={() => navigate("/support/tickets/new")}>
                      Raise a ticket
                    </Button>
                  )
                }
              />
            ),
          }}
          columns={[
            {
              title: "Subject",
              dataIndex: "subject",
              render: (subject: string) => <span style={{ fontWeight: 500 }}>{subject}</span>,
            },
            {
              title: "Status",
              dataIndex: "solved",
              width: 120,
              render: (v: boolean) =>
                v ? <StatusTag label="Solved" tone="ok" dot /> : <StatusTag label="Open" tone="warn" dot />,
            },
            {
              title: "Replies",
              dataIndex: "comments",
              width: 90,
              align: "right",
              render: (comments: TicketProps["comments"]) => (
                <span className="q-num" style={{ color: comments.length ? undefined : "var(--q-text-faint)" }}>
                  {comments.length}
                </span>
              ),
            },
            {
              title: "Last activity",
              dataIndex: "updatedAt",
              width: 140,
              align: "right",
              render: (v: string) => (
                <span className="q-num" style={{ color: "var(--q-text-muted)" }}>
                  {dayjs(v).format("D MMM YYYY")}
                </span>
              ),
            },
          ]}
        />
      </DisplayComponent>
    </PageShell>
  );
};
